import "./google-consent";
import { CONSENT_KEY, CONSENT_MAX_AGE, NO_OPTIONAL_TRACKING, createPrivacyChoice, parsePrivacyChoice, type PrivacyChoice, type PrivacyPreferences } from "./consent-state";

let memoryChoice: PrivacyChoice | null = null;
let storageUnavailable = false;
let choice: PrivacyChoice | null = readChoice();
const listeners = new Set<(preferences: PrivacyPreferences) => void>();
let expiryTimer: number | undefined;
function readChoice(): PrivacyChoice | null {
  if (storageUnavailable && memoryChoice) return parsePrivacyChoice(JSON.stringify(memoryChoice));
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    const parsed = parsePrivacyChoice(stored);
    if (stored && !parsed) localStorage.removeItem(CONSENT_KEY);
    return parsed;
  }
  catch { return memoryChoice && parsePrivacyChoice(JSON.stringify(memoryChoice)); }
}
export function onPrivacyChange(listener: (preferences: PrivacyPreferences) => void): void {
  listeners.add(listener);
  listener(choice ?? NO_OPTIONAL_TRACKING);
}

const dialog = document.createElement("dialog");
dialog.id = "privacy-dialog";
dialog.className = "privacy-dialog";
dialog.setAttribute("aria-labelledby", "privacy-heading");
dialog.setAttribute("aria-describedby", "privacy-description");
dialog.innerHTML = `
  <div class="privacy-dialog-heading">
    <h2 id="privacy-heading">Analytics settings</h2>
    <button type="button" class="privacy-close" aria-label="Close analytics settings">×</button>
  </div>
  <p id="privacy-description">Choose whether PostHog can collect analytics and record page interactions. Every audio tool works with these options turned off. Advertising consent is managed separately in Advertising privacy settings.</p>
  <div class="privacy-option privacy-required">
    <div><strong>Essential storage</strong><p>Saves your privacy choice on this browser for 180 days.</p></div><span>Always on</span>
  </div>
  <label class="privacy-option" for="privacy-analytics">
    <div><strong>Analytics &amp; feedback</strong><p>Allow PostHog to collect page visits, tool use and errors, and offer an optional feedback survey. Uses cookies and browser storage.</p></div>
    <input id="privacy-analytics" type="checkbox" />
  </label>
  <label class="privacy-option" for="privacy-replay">
    <div><strong>Session replay</strong><p>Allow PostHog to record interactions with the site. Audio, filenames and form inputs are masked or excluded. Requires analytics.</p></div>
    <input id="privacy-replay" type="checkbox" />
  </label>
  <details class="privacy-details">
    <summary>How we use your data</summary>
    <p>Read our <a href="/privacy/">privacy policy</a> for details about your audio and optional tracking.</p>
    <p>Audio you choose to process is uploaded to our server. Temporary audio files are deleted when the request finishes. This processing works independently of optional tracking.</p>
    <p>If you allow analytics, usage events and error reports are sent to PostHog's EU service. Filenames and audio contents are not included in these events. Session replay is enabled only if you also allow it.</p>
    <p>Changing your choice stops future optional collection and removes our PostHog cookies and stored identifiers. It does not erase data already sent. See <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog's privacy policy</a>.</p>
  </details>
  <p class="privacy-storage-note" role="status" hidden>Your browser could not save this choice. It applies on this page; you may be asked again on your next visit.</p>
  <div class="privacy-dialog-actions">
    <button class="button button-secondary" type="button" data-privacy-reject>Reject optional</button>
    <button class="button button-secondary" type="button" data-privacy-save>Save my choices</button>
  </div>`;
document.body.append(dialog);
const withdrawButtons = Array.from(document.querySelectorAll<HTMLElement>(".footer-privacy"), (footer) => {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "privacy-settings-link";
  button.setAttribute("data-privacy-withdraw", "");
  button.textContent = "Withdraw analytics consent";
  button.hidden = true;
  button.addEventListener("click", () => save(NO_OPTIONAL_TRACKING));
  footer.append(button);
  return button;
});
const analyticsInput = dialog.querySelector<HTMLInputElement>("#privacy-analytics")!;
const replayInput = dialog.querySelector<HTMLInputElement>("#privacy-replay")!;
const storageNote = dialog.querySelector<HTMLElement>(".privacy-storage-note")!;
let returnFocus: HTMLElement | null = null;

function updateReplayControl(): void {
  replayInput.disabled = !analyticsInput.checked;
  if (!analyticsInput.checked) replayInput.checked = false;
}
function updateControls(): void {
  for (const button of withdrawButtons) button.hidden = !choice?.analytics;
}
function scheduleExpiry(): void {
  window.clearTimeout(expiryTimer);
  if (!choice) return;
  const remaining = Math.max(0, choice.savedAt + CONSENT_MAX_AGE - Date.now());
  // Browser timers overflow beyond about 25 days; recheck until the fixed expiry.
  expiryTimer = window.setTimeout(refreshChoice, Math.min(remaining, 2_147_483_647));
}
function refreshChoice(): void {
  const next = readChoice();
  if (JSON.stringify(next) === JSON.stringify(choice)) { scheduleExpiry(); return; }
  choice = next;
  if (dialog.open) {
    analyticsInput.checked = choice?.analytics ?? false;
    replayInput.checked = choice?.replay ?? false;
    updateReplayControl();
  }
  notify();
}
function notify(): void {
  updateControls();
  scheduleExpiry();
  for (const listener of listeners) listener(choice ?? NO_OPTIONAL_TRACKING);
}
function openSettings(trigger?: HTMLElement): void {
  returnFocus = trigger ?? null;
  analyticsInput.checked = choice?.analytics ?? false;
  replayInput.checked = choice?.replay ?? false;
  updateReplayControl();
  if (!dialog.open) dialog.showModal();
}
function save(preferences: PrivacyPreferences): void {
  choice = createPrivacyChoice(preferences);
  memoryChoice = choice;
  let saved = false;
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(choice)); saved = true; storageUnavailable = false; }
  catch { storageUnavailable = true; }
  storageNote.hidden = saved;
  notify();
  if (saved) {
    dialog.close();
    const focusTarget = returnFocus?.isConnected ? returnFocus : document.querySelector<HTMLElement>("[data-privacy-settings]");
    focusTarget?.focus({ preventScroll: true });
  } else { openSettings(returnFocus ?? undefined); }
}
document.querySelectorAll<HTMLElement>("[data-privacy-settings]").forEach((button) => button.addEventListener("click", () => openSettings(button)));
analyticsInput.addEventListener("change", updateReplayControl);
dialog.querySelector("[data-privacy-reject]")!.addEventListener("click", () => save(NO_OPTIONAL_TRACKING));
dialog.querySelector("[data-privacy-save]")!.addEventListener("click", () => save({ analytics: analyticsInput.checked, replay: replayInput.checked }));
dialog.querySelector(".privacy-close")!.addEventListener("click", () => dialog.close());
dialog.addEventListener("close", () => {
  if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
});
// A withdrawal in another tab must stop collection in this tab too.
window.addEventListener("storage", (event) => {
  if (event.key === CONSENT_KEY || event.key === null) {
    memoryChoice = null;
    storageUnavailable = false;
    choice = readChoice();
    if (dialog.open) {
      analyticsInput.checked = choice?.analytics ?? false;
      replayInput.checked = choice?.replay ?? false;
      updateReplayControl();
    }
    notify();
  }
});
window.addEventListener("focus", refreshChoice);
document.addEventListener("visibilitychange", () => { if (!document.hidden) refreshChoice(); });
updateControls();
scheduleExpiry();
